import { useEffect, useState } from 'react';
import { mensajeDeError } from '@/core/red/conectividad';
import { Boton, EncabezadoPantalla, EncabezadoSeccion, Entrada, Error } from '@/ui/Sistema';
import { useAuth } from './AuthProvider';
import { darDeBajaMiCuenta, fetchAvisoDeBaja, type AvisoDeBaja } from './cuenta';
import { estadoDeLaCredencial } from '@/core/auth/sesionOffline';
import { ConfirmarAccion } from '@/ui/ConfirmarAccion';

/**
 * Mi cuenta, y la puerta de salida.
 *
 * Google Play exige que quien pueda crear una cuenta pueda borrarla, y que se
 * pueda hacer desde adentro de la aplicación. Esto es esa puerta.
 *
 * **Se explica antes de preguntar.** La baja no se deshace y no hace lo que la
 * palabra «borrar» sugiere: se lleva la identidad —correo, nombre, contraseña—
 * y deja el asiento contable, porque los trabajos y las liquidaciones también
 * son el libro de la veterinaria y no le pertenecen sólo a quien se va. Si eso
 * se descubre después de apretar, se descubrió tarde.
 *
 * **El aviso se pide al entrar, no al apretar.** Quien es el único
 * administrador de un equipo no puede irse sin pasar el mando; enterarse de eso
 * recién después de haber decidido irse y escrito el correo es avisar en el
 * peor momento.
 *
 * La confirmación es escribir el propio correo. No es seguridad —quien llegó
 * hasta acá ya tiene la sesión— sino un freno: un botón que borra una cuenta de
 * un toque se aprieta sin querer.
 */
export function MiCuentaPage({ onVolver }: { onVolver: () => void }) {
  const { session, signOut } = useAuth();
  const [aviso, setAviso] = useState<AvisoDeBaja | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [abierto, setAbierto] = useState(false);
  const [escrito, setEscrito] = useState('');
  const [dandoDeBaja, setDandoDeBaja] = useState(false);
  /**
   * Cerrar sesión se pregunta antes, y se dice lo que cuesta.
   *
   * Cerrar sesión destruye la credencial que permite abrir la aplicación sin
   * señal. Es lo correcto —quien se va, se va— y es imposible de adivinar desde
   * el botón: nadie relaciona «cerrar sesión» con «mañana en el campo no vas a
   * poder entrar».
   *
   * Eso fue exactamente lo que pasó. Se cerró sesión para probar el modo sin
   * señal, y cerrar sesión es justamente lo que lo desarma.
   */
  const [preguntandoSalir, setPreguntandoSalir] = useState(false);

  useEffect(() => {
    let cancelado = false;
    void (async () => {
      try {
        const a = await fetchAvisoDeBaja();
        if (!cancelado) setAviso(a);
      } catch (err) {
        if (!cancelado) setError(mensajeDeError(err));
      }
    })();
    return () => {
      cancelado = true;
    };
  }, []);

  const correo = aviso?.mi_correo ?? session?.user?.email ?? '';
  const coincide = escrito.trim().toLowerCase() === correo.trim().toLowerCase();

  const darDeBaja = async () => {
    setError(null);
    setDandoDeBaja(true);
    try {
      await darDeBajaMiCuenta(escrito.trim());
      // Se cierra la sesión enseguida: la cuenta ya no existe como identidad y
      // dejar la aplicación abierta mostrando datos de una organización que ya
      // no se integra es quedarse mirando algo que dejó de ser cierto.
      await signOut();
    } catch (err) {
      setError(mensajeDeError(err));
      setDandoDeBaja(false);
    }
  };

  return (
    <div className="flex flex-col gap-5 p-4" data-testid="mi-cuenta">
      <EncabezadoPantalla titulo="Mi cuenta" onVolver={onVolver} />

      <section className="superficie flex flex-col gap-2 p-4">
        <EncabezadoSeccion titulo="Sesión" />
        <p className="text-sm text-[var(--tinta-suave)]">Entrás con</p>
        <p className="break-all font-medium" data-testid="mi-correo">
          {correo || '—'}
        </p>
        <Boton
          className="mt-2 self-start"
          onClick={() => setPreguntandoSalir(true)}
          data-testid="cerrar-sesion"
        >
          Cerrar sesión
        </Boton>
        {preguntandoSalir && (
          <ConfirmarAccion
            titulo="Cerrar sesión"
            pregunta="¿Cerrás la sesión en este teléfono?"
            detalle="Para volver a entrar vas a necesitar señal: el teléfono no puede verificar una contraseña por su cuenta. Lo que tengas sin sincronizar se guarda igual y se envía cuando vuelvas."
            confirmar="Cerrar sesión"
            onConfirmar={() => {
              setPreguntandoSalir(false);
              void signOut();
            }}
            onCancelar={() => setPreguntandoSalir(false)}
            testId="confirmar-salir"
          />
        )}
        {/* Acá además de en la pantalla de ingreso: al reportar algo raro, lo
            primero que hace falta saber es qué versión está instalada, y quien
            ya entró no vuelve a ver la otra pantalla. */}
        <p className="mt-2 text-xs text-[var(--tinta-suave)]" data-testid="version">
          Versión {__VERSION__} · {estadoDeLaCredencial()}
        </p>
      </section>

      {error && <Error testId="cuenta-error">{error}</Error>}

      <section className="superficie flex flex-col gap-3 p-4" data-testid="baja">
        <EncabezadoSeccion titulo="Dar de baja la cuenta" />

        {/* Qué se lleva y qué deja, antes de cualquier botón. La palabra
            «borrar» promete más de lo que esto hace, y prometer de más acá es
            peor que quedarse corto. */}
        <p className="text-sm">
          Se borran tu correo, tu nombre y tu contraseña. No vas a poder volver a entrar con esta
          cuenta.
        </p>
        <p className="text-sm text-[var(--tinta-suave)]">
          Los trabajos que cargaste, las liquidaciones y lo que te tocó de cada reparto{' '}
          <strong className="font-medium text-[var(--tinta)]">quedan</strong>: son el libro de la
          veterinaria y también le sirven a la gente con la que trabajaste.
        </p>

        {aviso === null && !error && <p className="text-sm text-[var(--tinta-suave)]">Cargando…</p>}

        {aviso && !aviso.puede && (
          <div className="flex flex-col gap-1 text-sm" data-testid="pasar-el-mando">
            <p className="font-medium text-[var(--aviso)]">Antes tenés que pasar el mando.</p>
            <p className="text-[var(--tinta-suave)]">
              Sos el único administrador de{' '}
              {aviso.hay_que_pasar_el_mando.map((o) => o.name).join(', ')}, y ahí trabaja más gente.
              Hacé administrador a otra persona desde Equipo y volvé.
            </p>
          </div>
        )}

        {aviso?.puede && aviso.quedan_sin_nadie.length > 0 && (
          <p className="text-sm text-[var(--tinta-suave)]" data-testid="quedan-sin-nadie">
            {aviso.quedan_sin_nadie.map((o) => o.name).join(', ')} va a quedar sin nadie adentro. No
            se borra, pero ya no va a poder abrirla nadie.
          </p>
        )}

        {aviso?.puede && !abierto && (
          <Boton
            jerarquia="destructivo"
            className="self-start"
            onClick={() => setAbierto(true)}
            data-testid="quiero-darme-de-baja"
          >
            Dar de baja mi cuenta
          </Boton>
        )}

        {aviso?.puede && abierto && (
          <div className="flex flex-col gap-3" data-testid="confirmar-baja">
            <label className="flex flex-col gap-1 text-sm">
              Para confirmar, escribí <span className="font-medium">{correo}</span>
              <Entrada
                value={escrito}
                onChange={(e) => setEscrito(e.target.value)}
                autoComplete="off"
                autoCapitalize="none"
                spellCheck={false}
                inputMode="email"
                data-testid="confirmacion"
              />
            </label>
            <div className="flex flex-wrap gap-2">
              <Boton
                jerarquia="destructivo"
                disabled={!coincide || dandoDeBaja}
                onClick={() => void darDeBaja()}
                data-testid="confirmar"
              >
                {dandoDeBaja ? 'Dando de baja…' : 'Dar de baja definitivamente'}
              </Boton>
              <Boton
                onClick={() => {
                  setAbierto(false);
                  setEscrito('');
                }}
                data-testid="cancelar-baja"
              >
                Cancelar
              </Boton>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
